import React from 'react';
import PropTypes from 'prop-types';
import { Check, TemperatureHalf as Thermometer, WaveSquare } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import {
  SensorSectionContainer,
  ConfigSectionLabel,
  SensorSelectionGrid,
  SensorCardWrapper,
  SensorCardLeftWrapper,
  SensorCardLeft,
  SensorCheckIcon,
  SensorIconBox,
  SensorCardLabel,
  VibrationCardContainer,
  HelperText
} from './styled';
import FeatureDropdown from './FeatureDropdown';

const SensorSelectionSection = ({
  hasTemperature,
  hasVibration,
  hasTemperatureCondition,
  hasVibrationCondition,
  localConfig,
  alarmThresholds,
  speedBin,
  featureDropdownOpen,
  unavailableFeatures,
  getSensorTypeValue,
  checkTemperatureThresholdsAvailable,
  checkVibrationFeatureThresholdsAvailable,
  getFeatureUnavailableReason,
  onConditionTypeToggle,
  onFeatureDropdownToggle,
  onFeatureToggle
}) => {
  const { t } = useTranslation();
  const isTemperatureSelected = localConfig.coupled_sensor_types &&
    localConfig.coupled_sensor_types.includes(getSensorTypeValue('Temperature')) &&
    (!alarmThresholds || checkTemperatureThresholdsAvailable());

  const isTemperatureDisabled = !hasTemperatureCondition ||
    (alarmThresholds && !checkTemperatureThresholdsAvailable());

  const speedBinValue = speedBin || localConfig.speed_bin || 'bin_0';
  const hasVelocity = checkVibrationFeatureThresholdsAvailable('velocity', speedBinValue);
  const hasAcceleration = checkVibrationFeatureThresholdsAvailable('acceleration', speedBinValue);
  const hasAnyVibrationFeature = hasVelocity || hasAcceleration;

  const isVibrationSelected = localConfig.coupled_sensor_types &&
    localConfig.coupled_sensor_types.includes(getSensorTypeValue('Vibration')) &&
    (!alarmThresholds || hasAnyVibrationFeature);

  const isVibrationDisabled = !hasVibrationCondition ||
    (alarmThresholds && !hasAnyVibrationFeature);

  return (
    <SensorSectionContainer>
      <ConfigSectionLabel>{t('sensors.eventDetection.coupling.sensorsToCouple')}</ConfigSectionLabel>
      <SensorSelectionGrid>
        {/* Temperature Card */}
        {hasTemperature && (
          <SensorCardWrapper
            selected={isTemperatureSelected}
            disabled={isTemperatureDisabled}
            onClick={() => !isTemperatureDisabled && onConditionTypeToggle(getSensorTypeValue('Temperature'))}
          >
            <SensorCardLeft>
              <SensorCheckIcon checked={isTemperatureSelected}>
                <Check />
              </SensorCheckIcon>
              <SensorIconBox
                sensorType="temperature"
                selected={localConfig.coupled_sensor_types && localConfig.coupled_sensor_types.includes(getSensorTypeValue('Temperature'))}
              >
                <Thermometer />
              </SensorIconBox>
              <SensorCardLabel selected={localConfig.coupled_sensor_types && localConfig.coupled_sensor_types.includes(getSensorTypeValue('Temperature'))}>
                {t('sensors.eventDetection.temperature')}
              </SensorCardLabel>
            </SensorCardLeft>
          </SensorCardWrapper>
        )}

        {/* Vibration Card */}
        {hasVibration && (
          <VibrationCardContainer>
            <SensorCardWrapper
              selected={isVibrationSelected}
              disabled={isVibrationDisabled}
            >
              <SensorCardLeftWrapper
                disabled={isVibrationDisabled}
                onClick={() => !isVibrationDisabled && onConditionTypeToggle(getSensorTypeValue('Vibration'))}
              >
                <SensorCheckIcon checked={isVibrationSelected}>
                  <Check />
                </SensorCheckIcon>
                <SensorIconBox
                  sensorType="vibration"
                  selected={localConfig.coupled_sensor_types && localConfig.coupled_sensor_types.includes(getSensorTypeValue('Vibration'))}
                >
                  <WaveSquare />
                </SensorIconBox>
                <SensorCardLabel selected={localConfig.coupled_sensor_types && localConfig.coupled_sensor_types.includes(getSensorTypeValue('Vibration'))}>
                  {t('sensors.eventDetection.vibration')}
                </SensorCardLabel>
              </SensorCardLeftWrapper>

              {/* Inline Feature Dropdown */}
              {localConfig.coupled_sensor_types && localConfig.coupled_sensor_types.includes(getSensorTypeValue('Vibration')) && (
                <FeatureDropdown
                  isOpen={featureDropdownOpen}
                  vibrationFeatures={localConfig.vibration_features}
                  speedBin={speedBinValue}
                  alarmThresholds={alarmThresholds}
                  unavailableFeatures={unavailableFeatures}
                  checkVibrationFeatureThresholdsAvailable={checkVibrationFeatureThresholdsAvailable}
                  getFeatureUnavailableReason={getFeatureUnavailableReason}
                  getSensorTypeValue={getSensorTypeValue}
                  onToggle={onFeatureDropdownToggle}
                  onFeatureToggle={onFeatureToggle}
                />
              )}
            </SensorCardWrapper>
          </VibrationCardContainer>
        )}
      </SensorSelectionGrid>

      {(!hasTemperatureCondition && !hasVibrationCondition) && (
        <HelperText>
          {t('sensors.eventDetection.coupling.addConditionsHint')}
        </HelperText>
      )}
      {hasTemperature && !hasTemperatureCondition && hasVibrationCondition && (
        <HelperText>
          {t('sensors.eventDetection.coupling.addTemperatureConditionHint')}
        </HelperText>
      )}
      {hasVibration && !hasVibrationCondition && hasTemperatureCondition && (
        <HelperText>
          {t('sensors.eventDetection.coupling.addVibrationConditionHint')}
        </HelperText>
      )}
    </SensorSectionContainer>
  );
};

SensorSelectionSection.propTypes = {
  hasTemperature: PropTypes.bool.isRequired,
  hasVibration: PropTypes.bool.isRequired,
  hasTemperatureCondition: PropTypes.bool.isRequired,
  hasVibrationCondition: PropTypes.bool.isRequired,
  localConfig: PropTypes.object.isRequired,
  alarmThresholds: PropTypes.object,
  speedBin: PropTypes.string,
  featureDropdownOpen: PropTypes.bool.isRequired,
  unavailableFeatures: PropTypes.array,
  getSensorTypeValue: PropTypes.func.isRequired,
  checkTemperatureThresholdsAvailable: PropTypes.func.isRequired,
  checkVibrationFeatureThresholdsAvailable: PropTypes.func.isRequired,
  getFeatureUnavailableReason: PropTypes.func.isRequired,
  onConditionTypeToggle: PropTypes.func.isRequired,
  onFeatureDropdownToggle: PropTypes.func.isRequired,
  onFeatureToggle: PropTypes.func.isRequired
};

SensorSelectionSection.defaultProps = {
  alarmThresholds: null,
  speedBin: 'bin_0',
  unavailableFeatures: []
};

// Memoized because it has many props and all functions are now wrapped in useCallback
export default React.memo(SensorSelectionSection);
